/**
 * After a deploy, hashed chunks from the previous build are gone. The first
 * lazy import to miss throws (Vite raises `vite:preloadError`) — that isn't
 * an error for the person, it's "there's a newer version". Show a calm bar
 * with a refresh, and keep the old app usable underneath.
 */
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError|error loading dynamically imported module/i;

export function isStaleBuildError(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    return CHUNK_ERROR.test(msg);
}

let shown = false;
export function showUpdateBanner(): void {
    if (shown) return;
    shown = true;
    const bar = document.createElement("div");
    bar.className = "app-update-bar";
    bar.setAttribute("role", "status");
    bar.innerHTML = `<span>There's a new version of the app.</span><button type="button">Refresh to load</button>`;
    bar.querySelector("button")!.addEventListener("click", () => window.location.reload());
    document.body.appendChild(bar);
}

/**
 * Proactive check: every deploy changes the hashed entry script referenced by
 * index.html. Fetch the (2 KB) shell and compare that reference to the one we
 * booted with — whenever the tab comes back into view, and every 5 minutes
 * while visible. No server support needed; ETags aren't sent for the SPA root.
 */
let bootEntry: string | null | undefined;
async function entryScript(): Promise<string | null> {
    try {
        const res = await fetch("/", { cache: "no-store", credentials: "same-origin", headers: { accept: "text/html" } });
        const html = await res.text();
        return html.match(/\/assets\/index-[\w-]+\.js/)?.[0] ?? null;
    } catch { return null; }
}
async function checkForNewBuild(): Promise<void> {
    if (bootEntry === undefined) { bootEntry = await entryScript(); return; }
    if (!bootEntry) return;
    const now = await entryScript();
    if (now && now !== bootEntry) showUpdateBanner();
}

export function installUpdateListener(): void {
    window.addEventListener("vite:preloadError", (event) => {
        event.preventDefault();
        showUpdateBanner();
    });
    void checkForNewBuild();
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void checkForNewBuild(); });
    window.setInterval(() => { if (document.visibilityState === "visible") void checkForNewBuild(); }, 5 * 60_000);
}
